/**
 * AssetContextPanelV2
 *
 * Enhanced left panel with tabs: Upload | Gallery | Product
 * - Upload: Image upload + Brand selection
 * - Gallery: Recent generations
 * - Product: Product search with image selection
 *
 * @accessibility WCAG 2.1 AA compliant
 */

import { useState } from 'react';
import { Upload as UploadIcon, Images, Package } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AssetUploadTab } from './AssetUploadTab';
import { AssetGalleryTab } from './AssetGalleryTab';
import { AssetProductTab } from './AssetProductTab';
import type { ReferenceSlot } from './GenerationFormV2';

type TabId = 'upload' | 'gallery' | 'product';

interface AssetContextPanelV2Props {
  selectedFile: File | null;
  preview: string | null;
  onFileChange: (file: File | null) => void;
  selectedBrandId: string | null;
  onBrandChange: (brandId: string | null) => void;
  activeTab?: TabId;
  onTabChange?: (tab: TabId) => void;
  preselectedProductId?: string | null;
  onProductSelect?: (product: { id: string; name: string; sku?: string } | null) => void;
  mask?: Blob | null;
  referenceSlots?: ReferenceSlot[];
  onReferenceSlotsChange?: (slots: ReferenceSlot[]) => void;
  maxReferenceImages?: number;
  onInsertMention?: (tag: string) => void;
}

export function AssetContextPanelV2({
  selectedFile,
  preview,
  onFileChange,
  selectedBrandId,
  onBrandChange,
  activeTab: externalActiveTab,
  onTabChange: externalOnTabChange,
  preselectedProductId,
  onProductSelect,
  mask,
  referenceSlots,
  onReferenceSlotsChange,
  maxReferenceImages = 0,
  onInsertMention,
}: AssetContextPanelV2Props) {
  const [internalActiveTab, setInternalActiveTab] = useState<TabId>('upload');
  const activeTab = externalActiveTab !== undefined ? externalActiveTab : internalActiveTab;
  const setActiveTab = externalOnTabChange || setInternalActiveTab;

  const tabs = [
    { id: 'upload' as TabId, label: 'Upload', icon: UploadIcon },
    { id: 'gallery' as TabId, label: 'Galerie', icon: Images },
    { id: 'product' as TabId, label: 'Produkt', icon: Package },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Tab Navigation */}
      <div className="flex border-b border-neutral-200 dark:border-neutral-800">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors',
                'border-b-2 -mb-px',
                activeTab === tab.id
                  ? 'border-orange-500 text-orange-600 dark:text-orange-400'
                  : 'border-transparent text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100'
              )}
              aria-selected={activeTab === tab.id}
              role="tab"
            >
              <Icon className="h-4 w-4" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'upload' && (
          <AssetUploadTab
            selectedFile={selectedFile}
            preview={preview}
            onFileChange={onFileChange}
            selectedBrandId={selectedBrandId}
            onBrandChange={onBrandChange}
            mask={mask}
            referenceSlots={referenceSlots}
            onReferenceSlotsChange={onReferenceSlotsChange}
            maxReferenceImages={maxReferenceImages}
            onInsertMention={onInsertMention}
          />
        )}

        {activeTab === 'gallery' && (
          <AssetGalleryTab
            onImageSelect={onFileChange}
            selectedBrandId={selectedBrandId}
            onBrandChange={onBrandChange}
          />
        )}

        {activeTab === 'product' && (
          <AssetProductTab
            onImageSelect={(file) => {
              console.log('[AssetContextPanelV2] Product tab called onImageSelect, forwarding to onFileChange');
              onFileChange(file);
            }}
            selectedBrandId={selectedBrandId}
            onBrandChange={onBrandChange}
            preselectedProductId={preselectedProductId}
            onProductSelect={onProductSelect}
          />
        )}
      </div>
    </div>
  );
}
